import { Route } from 'react-router-dom';
import { Component, ReactNode } from 'react';
import { realmAuthProvider } from '@/providers/authProvider';
import { dataProvider } from '@/providers/dataProvider';
import {
  Admin,
  CustomRoutes,
  Resource,
  defaultDarkTheme,
  localStorageStore,
} from 'react-admin';
import { AdminLayout } from './AdminLayout';
import { CustomLoginPage } from './CustomLoginPage';
import { ErrorPage } from './ErrorPage';
import { TherapistsList } from '@/screens/therapists/TherapistsList';
import { AdminsList } from '@/screens/admins/AdminsList';
import { AppDocumentsList } from '@/screens/appDocuments/AppDocumentsList';
import { AppDocumentsEdit } from '@/screens/appDocuments/AppDocumentsEdit';
import { AppGlobalAlertsList } from '@/screens/appGlobalAlerts/AppGlobalAlertsList';
import { AppGlobalAlertsEdit } from '@/screens/appGlobalAlerts/AppGlobalAlertsEdit';
import { AppGlobalAlertsCreate } from '@/screens/appGlobalAlerts/AppGlobalAlertsCreate';
import { AIPromptsList } from '@/screens/aiPrompts/AIPromptsList';
import {
  AIPromptsCreate,
  AIPromptsEdit,
} from '@/screens/aiPrompts/AIPromptsEdit';
import { FrameworksList } from '@/screens/therapistSessionNotesFrameworks/FrameworksList';
import {
  FrameworksCreate,
  FrameworksEdit,
} from '@/screens/therapistSessionNotesFrameworks/FrameworksEdit';
import { TherapistSessionNotesList } from '@/screens/therapistSessionNotes/TherapistSessionNotesList';
import { TherapistAppConfig } from '@/screens/therapistAppConfig/TherapistAppConfig';
import { TherapistPatientsList } from '@/screens/therapistPatients/TherapistPatientsList';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { TherapistHomeworkList } from '@/screens/therapistHomework/TherapistHomeworkList';
import { DeadLetterQueueList } from '@/screens/deadLetterQueue/DeadLetterQueueList';
import { AppEventTrackerList } from '@/screens/appTrackerEvents/AppTrackerList';
import { DataExportsList } from '@/screens/data-exports/DataExportsList';
import { AIPromptTagsList } from '@/screens/aiPromptTags/AIPromptTagsList';
import {
  AIPromptTagsCreate,
  AIPromptTagsEdit,
} from '@/screens/aiPromptTags/AIPromptTagsEdit';
import { ConversionMonitorList } from '@/screens/conversionMonitor/ConversionMonitorList';
import { ReportsList } from '@/screens/reports/ReportsList';
import { ReportsDialog } from '@/screens/reports/ReportsDialog';

import { PrimeReactProvider } from 'primereact/api';

import 'primereact/resources/primereact.min.css';
import 'primereact/resources/themes/lara-light-cyan/theme.css';

const queryClient = new QueryClient();

const localStorageStoreVersion = '3';

const store = localStorageStore(localStorageStoreVersion);

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return <ErrorPage error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

function AdminApp() {
  return (
    <ErrorBoundary>
      <PrimeReactProvider>
        <QueryClientProvider client={queryClient}>
          <Admin
          store={store}
          authProvider={realmAuthProvider}
          dataProvider={dataProvider}
          requireAuth
          loginPage={<CustomLoginPage />}
          layout={AdminLayout}
          darkTheme={defaultDarkTheme}
          defaultTheme="dark"
          basename="/"
        >
          <Resource
            name="therapists"
            list={TherapistsList}
            options={{ label: 'Therapists', meta: { raw: false } }}
          />
          <Resource
            options={{ label: 'App Events Tracker' }}
            name="appTrackerEvents"
            list={AppEventTrackerList}
          />
          <Resource
            options={{ label: 'Data Exports' }}
            name="dataExports"
            list={DataExportsList}
          />
          <Resource
            options={{ label: 'Notes Frameworks' }}
            name="therapistSessionNotesFrameworks"
            list={FrameworksList}
            create={FrameworksCreate}
            edit={FrameworksEdit}
          />
          <Resource
            options={{ label: 'GPT Prompts' }}
            name="aiPrompts"
            list={AIPromptsList}
            edit={AIPromptsEdit}
            create={AIPromptsCreate}
          />
          <Resource
            options={{ label: 'Prompt Tags' }}
            name="aiPromptTags"
            list={AIPromptTagsList}
            create={AIPromptTagsCreate}
            edit={AIPromptTagsEdit}
          />
          <Resource
            options={{ label: 'App Documents' }}
            name="appDocuments"
            list={AppDocumentsList}
            edit={AppDocumentsEdit}
          />
          <Resource
            options={{ label: 'Global Alerts' }}
            name="appGlobalAlerts"
            list={AppGlobalAlertsList}
            create={AppGlobalAlertsCreate}
            edit={AppGlobalAlertsEdit}
          />
          <Resource
            name="deadLetterQueue"
            list={DeadLetterQueueList}
            options={{ label: 'Dead Letter Queue' }}
          />
          <Resource
            name="conversionMonitor"
            list={ConversionMonitorList}
            options={{ label: 'Conversion Monitor' }}
          />
          {/* 
    <Resource
    name="therapistTitles"
    options={{ label: "Therapists Titles" }}
    list={TherapistsTitlesList}
    create={TherapistTitleCreate}
    edit={TherapistTitleEdit}
    />
    <Resource
    name="whitelistedTherapists"
    options={{ label: "Therapists Whitelist" }}
    list={TherapistsWhitelistList}
    create={TherapistWhiteListCreate}
    />
    */}
          <Resource
            name="therapistPatients"
            options={{ label: 'Therapist Clients' }}
            list={TherapistPatientsList}
          />
          <Resource
            name="therapistSessionNotes"
            options={{ label: 'Therapist Session Notes' }}
            list={TherapistSessionNotesList}
          />
          <Resource
            name="therapistHomework"
            options={{ label: 'Therapist Homework' }}
            list={TherapistHomeworkList}
          />
          <Resource
            name="userMetadata"
            options={{ label: 'Administrators' }}
            list={AdminsList}
          />
          <CustomRoutes>
            <Route
              path="/therapist-app-admin"
              element={<TherapistAppConfig />}
            />
            <Route path="/reports/view/*" element={<ReportsDialog />} />
            <Route path="/reports/*" element={<ReportsList />} />
            {/* <Route path="/therapist-app-admin" /> */}
          </CustomRoutes>
        </Admin>
      </QueryClientProvider>
    </PrimeReactProvider>
    </ErrorBoundary>
  );
}

AdminApp.displayName = 'AdminApp';

export default AdminApp;
